import { useState, useCallback, useRef, useEffect, useMemo } from 'react';

import { employeeIncidentService } from '@/api/domains/employeeIncident';
import type { IncidentManagementItem } from '@/api/domains/employeeIncident';


const PAGE_SIZE = 20;

export interface IncidentBulkApprovalModalFilters {
  dateFrom?: string;
  dateTo?: string;
  errorTypeId?: number | null;
  filterEmployeeId?: number | null;
  searchText?: string;
  excludeIncidentIds?: number[];
}

export interface UseIncidentBulkApprovalModalListOptions {
  isSelectAllMode: boolean;
  /**
   * Modal visibility. Ambos modales masivos (aprobar/rechazar) permanecen montados
   * permanentemente y comparten el mismo `isSelectAllMode` a nivel de página — sin este flag,
   * activar "seleccionar todo" dispara el prefetch de página 1 en ambos hooks a la vez, aunque
   * solo uno de los dos modales vaya a abrirse (PR #320, comentario de revisión).
   */
  isOpen: boolean;
  filters?: IncidentBulkApprovalModalFilters;
}

export interface UseIncidentBulkApprovalModalListResult {
  allItems: IncidentManagementItem[];
  isLoadingMore: boolean;
  /**
   * Callback ref (no `useRef` object): el modal permanece montado mientras `isOpen` es `false`
   * y solo renderiza el `<div>` con scroll infinito cuando se abre — un `useRef` corriente no
   * dispararía el efecto que adjunta el listener de scroll si el nodo aparece después de que
   * el efecto ya corrió una vez. Un callback ref se ejecuta exactamente cuando React monta o
   * desmonta el nodo, sin depender de ningún prop adicional para saber cuándo reintentar.
   */
  scrollContainerRef: (node: HTMLDivElement | null) => void;
}

/**
 * Carga paginada (scroll infinito) de las incidencias Pendientes que cumplen los filtros
 * vigentes, usada exclusivamente para mostrar el listado dentro del modal de aprobación
 * masiva cuando está activo el modo "seleccionar todo" (ASIS-5249). Es puramente de
 * visualización — la aprobación real la resuelve el backend de forma independiente, con
 * los mismos filtros, al momento de ejecutar (ver useIncidentBulkApproval).
 * Mismo patrón que useBulkDisableModal (Ubicaciones).
 */
export function useIncidentBulkApprovalModalList({
  isSelectAllMode,
  isOpen,
  filters,
}: UseIncidentBulkApprovalModalListOptions): UseIncidentBulkApprovalModalListResult {
  const pageRef = useRef(1);
  const hasMoreRef = useRef(true);
  const isLoadingMoreRef = useRef(false);
  // Se incrementa cada vez que el efecto de reinicio corre (activación o cambio de filtros).
  // Una petición de `loadMore` en vuelo captura la generación vigente al arrancar; si al
  // resolver ya no coincide con la actual, se descarta — evita que una respuesta con filtros
  // obsoletos se agregue sobre un `allItems` recién vaciado y desincronice pageRef/hasMoreRef.
  const generationRef = useRef(0);
  const [allItems, setAllItems] = useState<IncidentManagementItem[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [scrollContainerNode, setScrollContainerNode] = useState<HTMLDivElement | null>(null);

  const loadMore = useCallback(async () => {
    if (!isSelectAllMode || !isOpen || isLoadingMoreRef.current || !hasMoreRef.current) return;
    const generation = generationRef.current;
    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);
    try {
      const response = await employeeIncidentService.listManagement({
        page: pageRef.current,
        pageSize: PAGE_SIZE,
        status: 'pendiente',
        dateFrom: filters?.dateFrom ?? null,
        dateTo: filters?.dateTo ?? null,
        errorTypeId: filters?.errorTypeId ?? null,
        filterEmployeeId: filters?.filterEmployeeId ?? null,
        searchText: filters?.searchText || null,
      });
      // Generación obsoleta: el reinicio ya vació allItems y reseteó pageRef/hasMoreRef para
      // los filtros actuales — aplicar esta respuesta la desincronizaría.
      if (generation !== generationRef.current) return;
      if (response.success && response.data) {
        setAllItems((prev) => [...prev, ...response.data!.items]);
        hasMoreRef.current = response.data.items.length === PAGE_SIZE;
        pageRef.current += 1;
      }
    } finally {
      if (generation === generationRef.current) {
        isLoadingMoreRef.current = false;
        setIsLoadingMore(false);
      }
    }
  }, [
    isSelectAllMode,
    isOpen,
    filters?.dateFrom,
    filters?.dateTo,
    filters?.errorTypeId,
    filters?.filterEmployeeId,
    filters?.searchText,
  ]);

  // Excluidos aplicados en la visualización (no al momento de traer los datos): así, si el
  // usuario deselecciona manualmente una fila ya cargada en páginas anteriores del scroll, esa
  // fila desaparece del listado sin necesidad de reiniciar la carga desde la página 1.
  const displayItems = useMemo(() => {
    const excludedIds = new Set(filters?.excludeIncidentIds ?? []);
    if (excludedIds.size === 0) return allItems;
    return allItems.filter((item) => !excludedIds.has(item.incidentId));
  }, [allItems, filters?.excludeIncidentIds]);

  // El modal permanece montado (solo oculto) mientras `isSelectAllMode` se activa/desactiva y
  // mientras los filtros de la pantalla cambian, así que un efecto de solo-montaje nunca
  // volvería a dispararse. Se reinicia el scroll infinito desde la página 1 cada vez que se
  // activa el modo "seleccionar todo" o que cambian los filtros vigentes mientras está activo
  // (p.ej. filtrar por RUT con la selección total ya encendida — ver ASIS-5249).
  // También depende de `isOpen`: el modal de aprobación y el de rechazo comparten el mismo
  // `isSelectAllMode` a nivel de página, así que sin este guard ambos dispararían el mismo
  // prefetch de página 1 en cuanto se activa "seleccionar todo" — aunque solo uno de los dos
  // vaya a abrirse (PR #320, comentario de revisión).
  useEffect(() => {
    if (!isSelectAllMode || !isOpen) return;
    generationRef.current += 1;
    pageRef.current = 1;
    hasMoreRef.current = true;
    isLoadingMoreRef.current = false;
    setAllItems([]);
    void loadMore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isSelectAllMode,
    isOpen,
    filters?.dateFrom,
    filters?.dateTo,
    filters?.errorTypeId,
    filters?.filterEmployeeId,
    filters?.searchText,
  ]);

  useEffect(() => {
    const el = scrollContainerNode;
    if (!el || !isSelectAllMode || !isOpen) return;

    const handleScroll = () => {
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
        void loadMore();
      }
    };

    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [scrollContainerNode, isSelectAllMode, isOpen, loadMore]);

  return {
    allItems: displayItems,
    isLoadingMore,
    scrollContainerRef: setScrollContainerNode,
  };
}

export default useIncidentBulkApprovalModalList;
